import type { SessionEvent } from '@aera/schema';
import { tidy } from '@aera/ui';
import { CheckCircle2 } from 'lucide-react';
import { lazy, Suspense, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '../components/ui.js';
import { LazyBriefSections } from '../brief/lazy.js';
import { SceneBoundary } from './Welcome.js';

const MorphField = lazy(() => import('@aera/ui/three').then((m) => ({ default: m.MorphField })));

export interface GenerationState {
  /** 'unsent': /finish did not get through, so nothing is being written yet; 'failed' is the server's status. */
  phase: 'starting' | 'internal' | 'client' | 'done' | 'review' | 'failed' | 'unsent';
  markdown: string;
  briefToken: string | null;
  reconnecting: boolean;
}

export const initialGeneration = (): GenerationState => ({ phase: 'starting', markdown: '', briefToken: null, reconnecting: false });

/**
 * The field's convergence from a progress event. The API reports progress on the whole 0–1 run already
 * (internal brief up to 0.5, client brief 0.55 to 1), so it is used as it comes and never moves back.
 */
export function progressAfter(current: number, event: Extract<SessionEvent, { type: 'progress' }>): number {
  if (event.phase === 'followups') return current;
  return Math.max(current, Math.min(1, event.progress));
}

/**
 * The brief streams in on the left while the particle field on the right converges from scattered to
 * ordered, driven by progress exactly as the landing page's MorphField is driven by scroll. With the review
 * gate on, the same screen plays while the internal brief is written and then hands over to the consultant.
 */
export function Generating({
  gen,
  progressRef,
  onRetry,
  contactable = true,
}: {
  gen: GenerationState;
  progressRef: MutableRefObject<number>;
  onRetry: () => void;
  /** The client ticked I5 and left a phone, WeChat or email: only then is the brief sent to them. */
  contactable?: boolean;
}) {
  const { t } = useTranslation();
  const writing = (gen.phase === 'client' || gen.phase === 'done') && gen.markdown.length > 0;
  return (
    <div className="mx-auto grid min-h-[calc(100svh-60px)] w-full max-w-5xl grid-rows-[200px_1fr] md:grid-cols-2 md:grid-rows-1">
      {/* The field keeps one viewport-high box on wide screens while the text column grows and scrolls:
          a canvas resized with the text would be cleared and re-centred out of view. */}
      <div className="relative order-1 overflow-hidden md:sticky md:top-0 md:order-2 md:h-[calc(100svh-60px)] md:self-start" aria-hidden>
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(circle at 50% 45%, color-mix(in srgb, var(--violet) 12%, transparent), color-mix(in srgb, var(--indigo) 5%, transparent) 40%, transparent 70%)' }}
        />
        <SceneBoundary>
          <Suspense fallback={null}>
            <MorphField progressRef={progressRef} className="scene-fade-edges absolute inset-0" />
          </Suspense>
        </SceneBoundary>
      </div>
      <main className="order-2 safe-x-4 pb-12 pt-4 md:order-1 md:safe-x-8 md:pt-16" aria-busy={gen.phase !== 'done' && gen.phase !== 'review' && gen.phase !== 'failed' && gen.phase !== 'unsent'}>
        {gen.phase === 'review' ? (
          <div className="max-w-[520px]">
            <CheckCircle2 className="h-8 w-8 text-indigo" aria-hidden />
            <h1 className="mt-4 text-[26px] font-semibold">{t('generating.reviewTitle')}</h1>
            <p className="mt-3 text-[17px] leading-relaxed text-ink-2">{tidy(t(contactable ? 'generating.review' : 'generating.reviewNoContact'))}</p>
          </div>
        ) : gen.phase === 'failed' ? (
          <div className="max-w-[520px]">
            <h1 className="text-[24px] font-semibold">{t('generating.failedTitle')}</h1>
            <p className="mt-3 text-[16px] leading-relaxed text-ink-2">{tidy(t('generating.failed'))}</p>
          </div>
        ) : gen.phase === 'unsent' ? (
          <div className="max-w-[520px]" role="alert">
            {/* Not generating.title ("Writing your brief"): nothing is being written yet. */}
            <h1 className="text-[24px] font-semibold">{t('generating.unsentTitle')}</h1>
            <p className="mt-3 text-[16px] leading-relaxed text-ink-2">{tidy(t('generating.unsent'))}</p>
            <Button variant="outline" className="mt-5" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          </div>
        ) : (
          <>
            <h1 className="text-[22px] font-semibold">{gen.phase === 'done' ? t('generating.done') : t('generating.title')}</h1>
            <div className="mt-3" aria-live="polite">
              {gen.phase !== 'done' && <Spinner label={gen.phase === 'client' ? t('generating.phaseClient') : t('generating.phaseInternal')} />}
            </div>
            {gen.reconnecting && <p className="mt-2 text-[13px] text-ink-2">{t('generating.reconnecting')}</p>}
            {writing && (
              <div className="mt-6">
                <Suspense fallback={<pre className="whitespace-pre-wrap font-sans text-[16px] leading-relaxed">{gen.markdown}</pre>}>
                  <LazyBriefSections markdown={gen.markdown} streaming={gen.phase === 'client'} />
                </Suspense>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
